import { TableColumn as WSRTableColumn } from '@wix/design-system';
import React, { ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { CollectionCard } from '../CollectionCard';
import { InitialLoader } from '../InitialLoader';
import { TableColumn } from '../../model';
import { InfiniteScrollTableState } from '../../state';
import { useTableCollectionSyncProps } from './useTableCollectionSyncProps';
import type { TableDragAndDrop } from '../TableDragAndDrop';
import {
  CollectionTableWSRTable,
  CollectionTableWSRTableBaseProps,
} from './CollectionTableWSRTable';
import { CollectionTableContent } from './CollectionTableContent';
import { PageInfiniteScrollLoader } from '../PageInfiniteScrollLoader';
import { useActionsCellColumn } from '../ActionsCell';
import {
  BulkActionModalRenderProp,
  BulkActionToolbarRenderProp,
  SingleCollectionBulkActionModal,
  SingleCollectionBulkActionToolbar,
} from '../BulkAction';
import { CollectionTableBaseCommonProps } from './CollectionTableBaseCommonProps';
import type { DataExtensionType } from '../DataExtension';
import { useCreateWSRColumnBase } from '../../hooks/useCreateWSRColumnBase';
import type { TagsElement } from '../Tags';
import { TablePlaceholderStates } from '../TablePlaceholderStates/TablePlaceholderStates';
import { useScrollableContentContext } from '../../providers';

export interface CollectionTableBaseProps<T, F extends FiltersMap>
  extends CollectionTableWSRTableBaseProps<T, T, F>,
    CollectionTableBaseCommonProps<T, F> {
  /**
   * Configuration of the table's columns.<br>
   * Each column needs to specify:
   *   * `id`: A unique identifier of the column (required for some features like custom/sortable columns)
   *   * `title`: a string or an element to display in the table header for this column
   *   * `render`: a function which will be called for every row in `data` to display this row's value for this column<br>
   *   * `sortable`: See [Sortable Columns](/pages/cairo/?path=/story/components-collection-features--sortable-columns)
   *
   * Read more on [Table API](https://www.docs.wixdesignsystem.com/?path=/story/components-lists-table--table)
   * @external
   */
  columns: TableColumn<T>[];

  /** Indicates whether to show a selection column (with checkboxes).<br>
   * Read more on [Table API](https://www.docs.wixdesignsystem.com/?path=/story/components-lists-table--table)
   * @default false
   * @external
   */
  showSelection?: boolean;
  /**
   * A callback method to be called on row click. Signature: `onRowClick(rowData, rowNum)`. To enable hover effect you should set this prop.
   * @external
   */
  onRowClick?: (item: T, index: number) => void;
  /**
   * A [`<ExportButton />`](/pages/cairo/?path=/story/components-collection-features--exportto) react element to be added to the toolbar
   */
  exportButton?: ReactElement;

  /**
   * Drag and drop implementation: `import { TableDragAndDrop } from '@wix/cairo';`
   * > In order to make the drag and drop also re-order the collection after drop, also add [OptimisticActions](/pages/cairo/?path=/story/components-collection-features--optimistic-actions).
   * @external
   */
  dragAndDrop?: typeof TableDragAndDrop;

  /**
   * Data extension implementation: `import { DataExtension } from '@wix/cairo';`
   * @external
   */
  dataExtension?: DataExtensionType;

  tags?: TagsElement<T>;

  /**
   * Render a custom toolbar when more than 1 item is selected.<br>
   * The function accepts the following parameters
   *   * `selectedValues`: The items that were checked via the selection column
   *   * `uncheckedValues`: The items that were unchecked via the selection column
   *   * `isSelectAll`: Indicates whether the "Select All" checkbox is checked
   *   * `openModal`: Opens the modal that contains the component from `bulkActionModal` render prop
   *   * `clearSelection`: Clears all existing selection, call this after the action on the selected items was done
   *   * `total`: Total items in the server (taken from [`fetchData`](/pages/cairo/?path=/story/common-hooks--usecollection) return value).
   *   * `openConfirmModal`: Opens a `<MessageModalLayout/>` to confirm an action about to be applied on the selected items.
   *   * `query`: ComputedQuery<F> - Representation of the current paging, sorting & filtering applied. Usually for using together with bulk action such as `updateAll` and `deleteAll`
   *
   * > When passed, enables `showSelection` by default.
   * @external
   */
  bulkActionToolbar?: BulkActionToolbarRenderProp<T, F>;
  /**
   * Render a modal to be opened from `bulkActionToolbar`.<br>
   * The function accepts the following parameters
   *   * `selectedValues`: The items that were checked via the selection column
   *   * `uncheckedValues`: The items that were unchecked via the selection column
   *   * `isSelectAll`: Indicates whether the "Select All" checkbox is checked
   *   * `closeModal`: Closes the modal
   *   * `query`: ComputedQuery<F> - Representation of the current paging, sorting & filtering applied. Usually for using together with bulk action such as `updateAll` and `deleteAll`
   * @external
   */
  bulkActionModal?: BulkActionModalRenderProp<T, F>;
  /**
   * @deprecated Enabled by default
   */
  useControlledFix?: boolean;
}

export interface CollectionTableProps<T, F extends FiltersMap>
  extends CollectionTableBaseProps<T, F> {
  state: InfiniteScrollTableState<T, F>;
}

function _CollectionTable<T, F extends FiltersMap = FiltersMap>(
  props: CollectionTableProps<T, F>,
) {
  const {
    state: tableState,
    filters,
    title,
    tabs,
    customColumns: customColumnsProp,
    multiLevelSorting: multiLevelSortingProp,
    dataExtension: dataExtensionProp,
    tags,
    views,
    extraToolbar,
    bulkActionToolbar,
    exportButton,
    exportModal,
    bulkActionModal,
    showTotal,
    sticky: stickyProp,
    actionsCell,
    actionsCellWidth,
    actionsCellProps,
    columns: columnsProp,
    onRowClick,
    onSortClick,
    showSelection: showSelectionProp,
    search,
    onSearchChange,
    onSearchClick,
    onSearchBlur,
    searchMaxLength,
    renderError,
    renderEmptyState,
    children,
    onSelectedItems,
    cardProps,
    minCardHeight,
    dataHook,
    topNotification,
    tableGridSwitchButton,
    useShowLoaderWhenEmpty,
    useNewInfiniteScrollLoader,
    dragAndDrop,
    dragAndDropBulkSubmit,
    dragAndDropSubmit,
    dragAndDropCancel,
    dragAndDropReorderModeToolbar,
    horizontalScroll: horizontalScrollProp,
    stickyColumns,
    useNewStickyColumns,
    hideBulkSelectionCheckbox,
    maxSelection,
    titleBarDisplay = true,
    BodyElementType,
    isApplyColumnWidthStyle,
    rowVerticalPadding,
    ...rest
  } = props;

  const state = tableState.toolbar;

  const showSelection = showSelectionProp ?? bulkActionToolbar != null;

  const horizontalScroll =
    horizontalScrollProp ??
    state.container.internalExperiments?.enabled(
      'specs.cairo.HorizontalScrollAsDefault',
    );

  const { scrollableContentRef } = useScrollableContentContext();

  const sticky = stickyProp ?? scrollableContentRef?.current != null;

  const { collection, internalScroll } = state;

  const customColumns =
    props.customColumns ||
    (dataExtensionProp && <dataExtensionProp.CustomColumns />);

  useTableCollectionSyncProps({
    ...props,
    dataExtension: dataExtensionProp,
    showSelection,
    customColumns,
    tags,
    isUsingTableVirtualization: !!BodyElementType,
  });

  const { selectedOrderedColumnsOrAll } = state;

  const { bulkSelect, query } = collection;

  const createWSRColumnBase = useCreateWSRColumnBase({ horizontalScroll });

  const transformedColumns: WSRTableColumn<KeyedItem<T>>[] =
    selectedOrderedColumnsOrAll.map((column, index, { length }) => {
      const {
        render,
        id,
        sortable,
        infoTooltipProps,
        title,
        width,
        style,
        ...restColumn
      } = column;

      const columnBase = createWSRColumnBase(column, index, length);

      return {
        ...restColumn,
        ...columnBase,
        ...(isApplyColumnWidthStyle ? undefined : { style, width }),
        sortDescending:
          sortable && id ? query.sort.getIsSortDescending(id) : undefined,
        render: (keyedItem: KeyedItem<T>, rowNum: number) =>
          render(keyedItem.item, rowNum),
      };
    });

  const actionsCellColumn = useActionsCellColumn({
    actionsCellProps: {
      ...(actionsCellProps ?? {}),
      stickyActionCell: horizontalScroll || actionsCellProps?.stickyActionCell,
    },
    actionsCellWidth,
    collection,
    sticky,
    actionsCell,
  });

  const columnsWithActions: WSRTableColumn<KeyedItem<T>>[] = actionsCellColumn
    ? [...transformedColumns, actionsCellColumn]
    : transformedColumns;

  const internalOnSortClick = (col: TableColumn<F>, index: number) => {
    onSortClick?.(col, index);

    if (col.id != null) {
      state.collection.sort(
        { id: col.id, sortMode: col.sortMode },
        {
          clearResult: true,
          multiple: multiLevelSortingProp !== undefined,
        },
      );
    }
  };

  let table = (
    <CollectionTableWSRTable
      {...props}
      customColumns={customColumns}
      rowVerticalPadding={rowVerticalPadding}
      dragAndDropReorderModeToolbar={dragAndDropReorderModeToolbar}
      selectionToolbar={
        bulkActionToolbar && (
          <SingleCollectionBulkActionToolbar
            state={state}
            bulkActionToolbar={bulkActionToolbar}
          />
        )
      }
      horizontalScroll={horizontalScroll}
      minCardHeight={minCardHeight}
      sticky={sticky}
      titleBarDisplay={titleBarDisplay}
      state={tableState}
      columns={columnsWithActions}
      onSortClick={internalOnSortClick}
      showSelection={showSelection}
      onSelectionChanged={(_selectedIds, _change) => {
        state.onSelectionChanged(_selectedIds, _change);
        onSelectedItems?.(bulkSelect.isSelectAll, bulkSelect.selectedValues);
        rest.onSelectionChanged?.(_selectedIds, _change);
      }}
      selectionDisabled={(row) => state.bulk.isRowCheckboxDisabled(row)}
      onRowClick={onRowClick && (({ item }, index) => onRowClick(item, index))}
      isApplyColumnWidthStyle={isApplyColumnWidthStyle}
    >
      <TablePlaceholderStates
        state={tableState}
        renderError={renderError}
        rowVerticalPadding={rowVerticalPadding}
        renderEmptyState={
          renderEmptyState
            ? () => {
                const {
                  result: { originQuery, hasAvailableItems },
                } = collection;
                return renderEmptyState({
                  hasAvailableItems,
                  query: originQuery,
                });
              }
            : undefined
        }
        topNotification={
          typeof topNotification === 'function'
            ? () => topNotification({ query: collection.result.originQuery })
            : topNotification
        }
      />

      <CollectionTableContent state={tableState}>
        {useNewInfiniteScrollLoader || internalScroll ? (
          <PageInfiniteScrollLoader
            dataHook="infinite-scroll-loader"
            state={collection}
            scrollMoreAlways
          />
        ) : undefined}
        {children}
      </CollectionTableContent>
    </CollectionTableWSRTable>
  );

  if (dragAndDrop) {
    const { CollectionTableDragAndDropContext } = dragAndDrop;
    table = (
      <CollectionTableDragAndDropContext
        state={state}
        onSubmit={dragAndDropSubmit}
        onCancel={dragAndDropCancel}
        onBulkSubmit={dragAndDropBulkSubmit}
      >
        {table}
      </CollectionTableDragAndDropContext>
    );
  }

  const tableSkeletonEnabled = state.container.internalExperiments?.enabled(
    'specs.cairo.TableSkeletonLoader',
  );

  return (
    <CollectionCard
      renderError={renderError}
      state={state}
      initialLoader={
        !tableSkeletonEnabled && (
          <InitialLoader
            dataHook="placeholder-states-loading-state"
            state={state}
            minHeight={minCardHeight}
          />
        )
      }
      dataHook={dataHook}
      dataExtension={dataExtensionProp}
      tags={tags}
      {...cardProps}
    >
      {exportModal ?? state.featuredComponents.exportModal.value}
      {bulkActionModal && (
        <SingleCollectionBulkActionModal
          state={state}
          bulkActionModal={bulkActionModal}
        />
      )}
      {table}
    </CollectionCard>
  );
}

export const CollectionTable = observer(_CollectionTable);
